import { Receipt, Download, CheckCircle, XCircle, Clock } from 'lucide-react';

export function TransactionsSettings() {
  const transactions = [
    {
      id: 'INV-2026-0204',
      date: 'Feb 4, 2026',
      description: 'Professional Plan - Monthly',
      amount: '$99.00',
      status: 'paid',
      paymentMethod: 'Visa •••• 4242',
    },
    {
      id: 'INV-2026-0104',
      date: 'Jan 4, 2026',
      description: 'Professional Plan - Monthly',
      amount: '$99.00',
      status: 'paid',
      paymentMethod: 'Visa •••• 4242',
    },
    {
      id: 'INV-2025-1204',
      date: 'Dec 4, 2025',
      description: 'Professional Plan - Monthly',
      amount: '$99.00',
      status: 'paid',
      paymentMethod: 'Visa •••• 4242',
    },
    {
      id: 'INV-2025-1104',
      date: 'Nov 4, 2025',
      description: 'Professional Plan - Monthly',
      amount: '$99.00',
      status: 'paid',
      paymentMethod: 'Visa •••• 4242',
    },
    {
      id: 'INV-2025-1004',
      date: 'Oct 4, 2025',
      description: 'Starter Plan - Monthly',
      amount: '$49.00',
      status: 'paid',
      paymentMethod: 'Visa •••• 4242',
    },
  ];

  const upcomingTransaction = {
    date: 'Mar 4, 2026',
    description: 'Professional Plan - Monthly',
    amount: '$99.00',
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-orange-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      paid: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      pending: 'bg-orange-100 text-orange-700',
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status as keyof typeof styles]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="max-w-6xl space-y-6">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-xl flex items-center justify-center shadow-md">
          <Receipt className="w-6 h-6 text-[#1875FF]" />
        </div>
        <div>
          <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight font-display">Transactions</h2>
          <p className="text-gray-600">View your billing history and download invoices</p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Next Payment</h3>
            </div>
            <p className="text-sm text-gray-700 mb-3">
              Your next payment will be processed on {upcomingTransaction.date}
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-gray-900 font-metric">{upcomingTransaction.amount}</span>
              <span className="text-sm text-gray-600">{upcomingTransaction.description}</span>
            </div>
          </div>
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
            Update Payment Method
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-extrabold text-gray-900 font-display">Payment History</h3>
            <button className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors">
              <Download className="w-4 h-4" />
              Export All
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-600">Invoice</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-600">Date</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-600">Description</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-600">Payment Method</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-600">Amount</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-600">Status</th>
                <th className="text-left py-3 px-6 text-sm font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50 transition-colors">
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      <Receipt className="w-4 h-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{transaction.id}</span>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-gray-700">{transaction.date}</td>
                  <td className="py-4 px-6 text-sm text-gray-700">{transaction.description}</td>
                  <td className="py-4 px-6 text-sm text-gray-600">{transaction.paymentMethod}</td>
                  <td className="py-4 px-6 text-sm font-medium text-gray-900">{transaction.amount}</td>
                  <td className="py-4 px-6">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(transaction.status)}
                      {getStatusBadge(transaction.status)}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <button className="flex items-center gap-1 text-sm text-[#1875FF] hover:text-[#05E0D1] transition-colors">
                      <Download className="w-4 h-4" />
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Showing {transactions.length} transactions</span>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 text-gray-600 hover:bg-white rounded transition-colors">
                Previous
              </button>
              <button className="px-3 py-1 text-gray-600 hover:bg-white rounded transition-colors">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-lg font-extrabold text-gray-900 font-display mb-4">Billing Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="text-sm text-gray-600 mb-1">Total Paid (2026)</div>
            <div className="text-2xl font-bold text-gray-900 font-metric">$198.00</div>
            <div className="text-xs text-green-600 mt-1">2 payments</div>
          </div>
          <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-200">
            <div className="text-sm text-gray-600 mb-1">Total Paid (2025)</div>
            <div className="text-2xl font-bold text-gray-900 font-metric">$643.00</div>
            <div className="text-xs text-blue-600 mt-1">10 payments</div>
          </div>
          <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-200">
            <div className="text-sm text-gray-600 mb-1">Next Payment</div>
            <div className="text-2xl font-bold text-gray-900 font-metric">$99.00</div>
            <div className="text-xs text-purple-600 mt-1">Due Mar 4, 2026</div>
          </div>
        </div>
      </div>
    </div>
  );
}
